"use client";

/**
 * 브라우저 전용 E2E 암호화(기술스펙 4.2). window.crypto.subtle(WebCrypto)로만 동작하고,
 * 개인키는 여기서 만든 뒤 절대 서버로 전송하지 않는다 — 공개키만 서버(listing_identity_keys.public_key,
 * Agenzax_E2E_멀티키_설계.md)에 등록. 리스팅당 이 키가 유일하다는 전제는 폐기됐다 — 에이전트와
 * 오너의 각 브라우저가 각자 독립적으로 이 모듈로 키를 만들고, 전부 별개의 키 보유자로 등록된다.
 *
 * 알고리즘: 신원 키는 RSA-OAEP-2048(비대칭, 세션키를 상대 공개키로 감싸는 용도),
 * 세션/메시지 본문은 AES-256-GCM(대칭). 둘 다 WebCrypto 표준 알고리즘이라 자체 암호를 만들지
 * 않는다(기술스펙 4.2.1 "자기만의 암호를 만들지 말라" 원칙).
 *
 * 개인키를 브라우저 localStorage에 리스팅 id별로 저장하는 단순화된 버전이다(기술스펙이 설명하는
 * "로그인 자격증명으로 암호화한 백업"은 OAuth 전용 로그인이라 적용 불가로 판단해 폐기, 설계
 * 문서 참고) — 즉 브라우저를 바꾸거나 localStorage를 지우면 그 디바이스로는 과거 대화를 복호화할
 * 수 없다(다른 디바이스가 남아있다면 그쪽에서 여전히 열람 가능 — 멀티 키 설계의 핵심).
 */

const RSA_ALG = { name: "RSA-OAEP", hash: "SHA-256" } as const;
const AES_ALG = "AES-GCM";

function storageKey(listingId: string): string {
  return `agenzax_e2e_privkey_${listingId}`;
}

/** 로컬 개인키가 서버의 어떤 유효한(비폐기) 키와도 대응되지 않을 때(폐기됐거나 손상된 경우) 이
 * 개인키를 버리기 위한 용도 — 호출부(conversation-client.ts)가 새 키를 자동으로 다시 발급받게 한다. */
export function clearStoredIdentityKey(listingId: string): void {
  localStorage.removeItem(storageKey(listingId));
}

export async function generateIdentityKeyPair(): Promise<CryptoKeyPair> {
  return window.crypto.subtle.generateKey({ ...RSA_ALG, modulusLength: 2048, publicExponent: new Uint8Array([1, 0, 1]) }, true, [
    "encrypt",
    "decrypt",
  ]);
}

export async function exportPublicKey(key: CryptoKey): Promise<ArrayBuffer> {
  return window.crypto.subtle.exportKey("spki", key);
}

export async function importPublicKey(spki: ArrayBuffer): Promise<CryptoKey> {
  return window.crypto.subtle.importKey("spki", spki, RSA_ALG, true, ["encrypt"]);
}

async function exportPrivateKey(key: CryptoKey): Promise<ArrayBuffer> {
  return window.crypto.subtle.exportKey("pkcs8", key);
}

async function importPrivateKey(pkcs8: ArrayBuffer): Promise<CryptoKey> {
  return window.crypto.subtle.importKey("pkcs8", pkcs8, RSA_ALG, false, ["decrypt"]);
}

/** 리스팅별 신원 키를 localStorage에서 찾고 없으면 새로 만든다(신규 생성 시 공개키도 함께 반환 — 호출부가 서버에 등록해야 함). */
export async function loadOrCreateIdentityKey(
  listingId: string
): Promise<{ privateKey: CryptoKey; publicKeySpki: ArrayBuffer | null }> {
  const { bufferToBase64, base64ToBuffer } = await import("./binary");
  const existing = localStorage.getItem(storageKey(listingId));
  if (existing) {
    const privateKey = await importPrivateKey(base64ToBuffer(existing));
    return { privateKey, publicKeySpki: null };
  }

  const pair = await generateIdentityKeyPair();
  const pkcs8 = await exportPrivateKey(pair.privateKey);
  localStorage.setItem(storageKey(listingId), bufferToBase64(pkcs8));
  const spki = await exportPublicKey(pair.publicKey);
  return { privateKey: pair.privateKey, publicKeySpki: spki };
}

/**
 * 멀티 키 기능(Agenzax_E2E_멀티키_설계.md) 도입 이전에 이미 만들어진 개인키는 서버가 발급한
 * key_holder_id를 로컬에 저장해둔 적이 없다 — 그 매핑을 복구하려면 저장된 개인키에서 공개키를
 * 다시 구해 서버에 등록된 값과 대조해야 한다. RSA는 개인키(PKCS8)에 공개 성분(n, e)이 그대로
 * 들어있어서, extractable하게 다시 임포트한 뒤 JWK로 내보내면 n·e만 뽑아 공개키를 재구성할 수
 * 있다 — 새 키를 만들거나 서버에 새로 등록하지 않고 순수 로컬 계산만으로 끝난다.
 */
export async function derivePublicKeyFromStoredPrivateKey(listingId: string): Promise<ArrayBuffer | null> {
  const stored = localStorage.getItem(storageKey(listingId));
  if (!stored) return null;
  const { base64ToBuffer } = await import("./binary");
  const extractablePrivateKey = await window.crypto.subtle.importKey("pkcs8", base64ToBuffer(stored), RSA_ALG, true, ["decrypt"]);
  const jwk = await window.crypto.subtle.exportKey("jwk", extractablePrivateKey);
  const publicJwk: JsonWebKey = { kty: jwk.kty, n: jwk.n, e: jwk.e, alg: jwk.alg, ext: true };
  const publicKey = await window.crypto.subtle.importKey("jwk", publicJwk, RSA_ALG, true, ["encrypt"]);
  return window.crypto.subtle.exportKey("spki", publicKey);
}

export async function generateSessionKey(): Promise<CryptoKey> {
  return window.crypto.subtle.generateKey({ name: AES_ALG, length: 256 }, true, ["encrypt", "decrypt"]);
}

async function exportSessionKeyRaw(key: CryptoKey): Promise<ArrayBuffer> {
  return window.crypto.subtle.exportKey("raw", key);
}

async function importSessionKeyRaw(raw: ArrayBuffer): Promise<CryptoKey> {
  return window.crypto.subtle.importKey("raw", raw, AES_ALG, false, ["encrypt", "decrypt"]);
}

/** 세션 대칭키를 상대(또는 내 자신)의 RSA 공개키로 감싼다 — open_conversation에 실어 보낼 값. */
export async function wrapSessionKeyForRecipient(sessionKey: CryptoKey, recipientPublicKey: CryptoKey): Promise<ArrayBuffer> {
  const raw = await exportSessionKeyRaw(sessionKey);
  return window.crypto.subtle.encrypt(RSA_ALG, recipientPublicKey, raw);
}

/** 내 개인키로 세션키를 풀어 실제 사용 가능한 AES 키로 복원한다. */
export async function unwrapSessionKey(encryptedSessionKey: ArrayBuffer, myPrivateKey: CryptoKey): Promise<CryptoKey> {
  const raw = await window.crypto.subtle.decrypt(RSA_ALG, myPrivateKey, encryptedSessionKey);
  return importSessionKeyRaw(raw);
}

/**
 * unwrapSessionKey와 똑같이 풀지만, 추출 가능한(extractable) 상태로 복원한다. 백필 응답자
 * (Agenzax_E2E_멀티키_설계.md 5절 — 과거 세션키를 새 키 보유자의 공개키로 다시 감싸 넘겨주는
 * 쪽)만 이 함수를 써야 한다: wrapSessionKeyForRecipient가 내부적으로 raw 바이트를 다시 뽑아야
 * 하는데 일반 unwrapSessionKey가 만드는 키는(재추출 불가, 보안상 기본값) 그걸 막기 때문이다.
 * 평소 메시지 복호화(대화방 열람)에는 이 함수를 쓰지 말 것 — 불필요하게 노출 표면을 넓힌다.
 */
export async function unwrapSessionKeyExtractable(encryptedSessionKey: ArrayBuffer, myPrivateKey: CryptoKey): Promise<CryptoKey> {
  const raw = await window.crypto.subtle.decrypt(RSA_ALG, myPrivateKey, encryptedSessionKey);
  return window.crypto.subtle.importKey("raw", raw, AES_ALG, true, ["encrypt", "decrypt"]);
}

export async function encryptMessage(sessionKey: CryptoKey, plaintext: string): Promise<{ ciphertext: ArrayBuffer; iv: ArrayBuffer }> {
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const encoded = new TextEncoder().encode(plaintext);
  const ciphertext = await window.crypto.subtle.encrypt({ name: AES_ALG, iv }, sessionKey, encoded);
  return { ciphertext, iv: iv.buffer };
}

/** Gate4 필수 실패 시나리오: 잘못된 키로 복호화 시 GCM 인증 태그 검증에 실패해 예외가 던져진다
 * (깨진 문자를 조용히 반환하지 않음) — 호출부가 이 예외를 "복호화 실패"로 명시 표시해야 한다. */
export async function decryptMessage(sessionKey: CryptoKey, ciphertext: ArrayBuffer, iv: ArrayBuffer): Promise<string> {
  const plainBuf = await window.crypto.subtle.decrypt({ name: AES_ALG, iv: new Uint8Array(iv) }, sessionKey, ciphertext);
  return new TextDecoder().decode(plainBuf);
}
